import type { Member, OrgRole } from "../types";

/**
 * The rules for changing someone's membership (FR-ADMIN-010, 118), as `crates/services/identity/src/members.rs`
 * applies them. The UI uses them to offer only the changes the service will accept; the mock enforces them.
 */

export type MemberChange =
  | { kind: "role"; role: OrgRole }
  | { kind: "suspend" }
  | { kind: "reactivate" }
  | { kind: "remove" };

export type Refusal = { kind: "permission" | "conflict" | "validation"; message: string };

export interface Party {
  id: string;
  role: OrgRole;
}

export const LAST_OWNER_MESSAGE =
  "This is the organisation's last Owner. Make an Admin an Owner first, so the organisation keeps one.";

/** Undefined when `actor` (an Owner or Admin) may make `change` to `target`; otherwise why not. */
export function memberChangeRefusal(
  actor: Party,
  target: Party,
  change: MemberChange,
  owners: number,
): Refusal | undefined {
  if (change.kind === "suspend" && actor.id === target.id)
    return { kind: "validation", message: "You can't suspend yourself." };
  const makesOwner = change.kind === "role" && change.role === "owner" && target.role !== "owner";
  if ((target.role === "owner" || makesOwner) && actor.role !== "owner")
    return { kind: "permission", message: "Owner role needed to change an Owner or make one." };
  if (makesOwner && target.role !== "admin")
    return {
      kind: "validation",
      message: "Ownership moves only to an existing Admin. Make them an Admin first.",
    };
  const unseatsOwner =
    target.role === "owner" &&
    (change.kind === "suspend" ||
      change.kind === "remove" ||
      (change.kind === "role" && change.role !== "owner"));
  if (unseatsOwner && owners <= 1) return { kind: "conflict", message: LAST_OWNER_MESSAGE };
  return undefined;
}

/** Active Owners in a member list. */
export function ownerCount(members: readonly Member[]): number {
  return members.filter((member) => member.role === "owner" && member.status === "active").length;
}
